import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';

const CountdownOverlay = ({ onCountdownEnd }) => {
  // Production mode: countdown to real date
  const [testMode] = useState(false);
  const [countdownStarted] = useState(true);
  
  // Target date: Sunday, November 30, 2025 23:59:59 CET
  const targetDate = new Date('2025-11-30T23:59:59+01:00');
  
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Check if countdown has already ended
    const countdownEnded = localStorage.getItem('countdownEnded');
    if (countdownEnded === 'true') {
      onCountdownEnd();
      return;
    }

    if (!countdownStarted && testMode) {
      return; // Don't start countdown until button is clicked
    }

    const calculateTimeLeft = () => {
      let difference;
      
      if (testMode && countdownStarted) {
        // Test mode: 30 seconds from when button was clicked
        const startTime = parseInt(localStorage.getItem('testStartTime') || Date.now());
        const endTime = startTime + (30 * 1000); // 30 seconds
        difference = endTime - Date.now();
      } else {
        // Real mode: until target date
        difference = targetDate.getTime() - Date.now();
      }

      if (difference <= 0) {
        localStorage.setItem('countdownEnded', 'true');
        onCountdownEnd();
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      if (newTimeLeft) {
        setTimeLeft(newTimeLeft);
      }
    }, 1000);

    // Initial calculation
    const initial = calculateTimeLeft();
    if (initial) {
      setTimeLeft(initial);
    }

    return () => clearInterval(timer);
  }, [targetDate, onCountdownEnd, testMode, countdownStarted]);

  const startTestCountdown = () => {
    localStorage.setItem('testStartTime', Date.now().toString());
    setCountdownStarted(true);
  };

  const switchToRealMode = () => {
    localStorage.removeItem('testStartTime');
    setTestMode(false);
    setCountdownStarted(true);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
      <div className="w-full h-full flex flex-col items-center justify-center p-4 md:p-8 gap-8 pt-20 md:pt-24">
        {/* E-Ink Display Image */}
        <div className="relative max-w-4xl w-full px-4 flex-shrink-0">
          <img 
            src="/eink-message.jpg" 
            alt="E-Ink Message" 
            className="w-full h-auto rounded-2xl shadow-2xl"
          />
        </div>

        {/* Countdown Display */}
        <div className="flex-shrink-0">
          {/* Desktop: 4 boxes in a row */}
          <div className="hidden md:flex gap-4 lg:gap-6 justify-center">
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4 lg:p-6 min-w-[80px] lg:min-w-[100px]">
                <div className="text-4xl lg:text-5xl font-black text-accent-primary mb-2">
                  {String(timeLeft.days).padStart(2, '0')}
                </div>
                <div className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider">Dias</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4 lg:p-6 min-w-[80px] lg:min-w-[100px]">
                <div className="text-4xl lg:text-5xl font-black text-accent-primary mb-2">
                  {String(timeLeft.hours).padStart(2, '0')}
                </div>
                <div className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider">Horas</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4 lg:p-6 min-w-[80px] lg:min-w-[100px]">
                <div className="text-4xl lg:text-5xl font-black text-accent-primary mb-2">
                  {String(timeLeft.minutes).padStart(2, '0')}
                </div>
                <div className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider">Minutos</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4 lg:p-6 min-w-[80px] lg:min-w-[100px]">
                <div className="text-4xl lg:text-5xl font-black text-accent-primary mb-2">
                  {String(timeLeft.seconds).padStart(2, '0')}
                </div>
                <div className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider">Segundos</div>
              </div>
            </div>
          </div>
          
          {/* Mobile: 2x2 grid */}
          <div className="grid grid-cols-2 gap-3 md:hidden max-w-xs mx-auto">
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4">
                <div className="text-3xl font-black text-accent-primary mb-1">
                  {String(timeLeft.days).padStart(2, '0')}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Dias</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4">
                <div className="text-3xl font-black text-accent-primary mb-1">
                  {String(timeLeft.hours).padStart(2, '0')}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Horas</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4">
                <div className="text-3xl font-black text-accent-primary mb-1">
                  {String(timeLeft.minutes).padStart(2, '0')}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Minutos</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 border-2 border-accent-primary rounded-xl p-4">
                <div className="text-3xl font-black text-accent-primary mb-1">
                  {String(timeLeft.seconds).padStart(2, '0')}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Segundos</div>
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon Text */}
        <div className="text-center flex-shrink-0 px-4">
          <h2 className="text-xl md:text-2xl font-bold text-accent-primary mb-2">
            Algo especial está chegando...
          </h2>
          <p className="text-gray-400 text-sm md:text-base">
            Até Domingo, 30 de Novembro de 2025, 20:00
          </p>
        </div>
      </div>
    </div>
  );
};

export default CountdownOverlay;
